// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2024 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import openPrintDialog from "@insite/client-framework/Common/Utilities/openPrintDialog";
import siteMessage from "@insite/client-framework/SiteMessage";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import setAddToListModalIsOpen from "@insite/client-framework/Store/Components/AddToListModal/Handlers/SetAddToListModalIsOpen";
import { getSettingsCollection } from "@insite/client-framework/Store/Context/ContextSelectors";
import { canAddAllToList, getCartState } from "@insite/client-framework/Store/Data/Carts/CartsSelector";
import addToWishList from "@insite/client-framework/Store/Data/WishLists/Handlers/AddToWishList";
import translate from "@insite/client-framework/Translate";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import Link, { LinkPresentationProps } from "@insite/mobius/Link";
import ToasterContext from "@insite/mobius/Toast/ToasterContext";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import { XNGAGE_CUSTOM_WIDGET } from "@sc/XDL/constants";
import * as React from "react";
import { FC } from "react";
import { connect, ResolveThunks } from "react-redux";
import { css } from "styled-components";

const mapStateToProps = (state: ApplicationState) => {
    const cart = getCartState(state, state.pages.onePageCheckoutConfirmation.cartId)?.value;
    return {
        wishListSettings: getSettingsCollection(state).wishListSettings,
        cart,
        canAddAllToList: canAddAllToList(cart),
    };
};

const mapDispatchToProps = {
    setAddToListModalIsOpen,
    addToWishList,
};

type Props = WidgetProps & ReturnType<typeof mapStateToProps> & ResolveThunks<typeof mapDispatchToProps>;

export interface OnePageCheckoutConfirmationActionsStyles {
    wrapper?: InjectableCss;
    addAllToListLink?: LinkPresentationProps;
    printLink?: LinkPresentationProps;
}

export const onePageCheckoutConfirmationActionsStyles: OnePageCheckoutConfirmationActionsStyles = {};

const styles = onePageCheckoutConfirmationActionsStyles;

const OnePageCheckoutConfirmationActions: FC<Props> = ({
    cart,
    wishListSettings,
    canAddAllToList,
    setAddToListModalIsOpen,
    addToWishList,
}) => {
    const toasterContext = React.useContext(ToasterContext);

    if (!cart) {
        return null;
    }
    const addAllToListClickHandler = () => {
        if (!wishListSettings) {
            return;
        }

        const productInfos =
            cart.cartLines
                ?.filter(o => o.productId && o.qtyOrdered)
                .map(cartLine => ({
                    productId: cartLine.productId!,
                    qtyOrdered: cartLine.qtyOrdered!,
                    unitOfMeasure: cartLine.unitOfMeasure,
                })) || [];
        if (!wishListSettings.allowMultipleWishLists) {
            addToWishList({
                productInfos,
                onSuccess: () => {
                    toasterContext.addToast({ body: siteMessage("Lists_ProductAdded"), messageType: "success" });
                },
                onComplete(resultProps) {
                    if (resultProps.result?.wishList) {
                        // "this" is targeting the object being created, not the parent SFC
                        // eslint-disable-next-line react/no-this-in-sfc
                        this.onSuccess?.(resultProps.result.wishList);
                    } else if (resultProps.result?.errorMessage) {
                        toasterContext.addToast({ body: resultProps.result.errorMessage, messageType: "danger" });
                    }
                },
            });
            return;
        }

        setAddToListModalIsOpen({ modalIsOpen: true, productInfos });
    };

    return (
        <StyledWrapper {...styles.wrapper}>
            <Link {...styles.printLink} onClick={openPrintDialog} aria-label="Print Order Request">
                {translate("Print Order Request")}
            </Link>
            <Link
                {...styles.addAllToListLink}
                onClick={addAllToListClickHandler}
                disabled={!canAddAllToList}
                aria-label="Add All to List"
            >
                {translate("Add All to List")}
            </Link>
        </StyledWrapper>
    );
};

const widgetModule: WidgetModule = {
    component: connect(mapStateToProps, mapDispatchToProps)(OnePageCheckoutConfirmationActions),
    definition: {
        displayName: "Order Confirmation Actions",
        group: XNGAGE_CUSTOM_WIDGET,
        allowedContexts: ["OnePageCheckoutConfirmationPage"],
    },
};

export default widgetModule;
